/**
 * On-device translation, using the browser's own translator.
 *
 * Chrome and Edge 138+ ship a `Translator` and a `LanguageDetector` that run a
 * local model — no key, no quota, no cost, and, the part that actually decided
 * this: the text never leaves the agent's machine. What is in these bubbles is
 * a customer's, often with an order number or an address in it, and posting it
 * to a free translation endpoint to save an agent a copy-paste is not a trade
 * this product gets to make on their behalf.
 *
 * The price is coverage, and Safari is the whole of it: it ships neither API,
 * so on a Mac this path is simply not there. Where the server has been given an
 * `ANTHROPIC_API_KEY` those browsers fall back to it — one bubble at a time,
 * never the thread — and where it has not, there is no button at all rather
 * than one that always fails. See `translationSupported`.
 *
 * The order is never negotiable: on-device is tried first every time it exists,
 * and the server is only ever asked by a browser that cannot do the job itself.
 *
 * Everything here is session state by design. The model handles live in module
 * scope and the results cache is a plain Map: closing the tab loses all of it,
 * nothing is written to storage, and nothing is sent to the server. A reload is
 * a fresh start, which is what "session based" has to mean if it means anything.
 */

const TARGET_LANGUAGE = 'en';

/**
 * Both APIs, not just the translator: a bubble does not say what language it is
 * in, so detection is what makes the button a single click rather than a
 * language picker the agent has to answer before they can read anything.
 */
export function translationAvailable() {
  return typeof self !== 'undefined' && 'Translator' in self && 'LanguageDetector' in self;
}

/**
 * Whether a translate control is worth rendering at all.
 *
 * Asked per render rather than once at module load: `hasServer` arrives from
 * `/config` a moment after the thread first paints, and a button that appears
 * when the answer comes back is better than one that never does.
 */
export function translationSupported(hasServer = false) {
  return translationAvailable() || Boolean(hasServer);
}

/** Detectors and translators are expensive to build and cheap to keep. */
let detectorPromise = null;
const translators = new Map();

/** messageKey -> translated text, for the life of this tab. */
const results = new Map();

/**
 * An edit invalidates its own translation.
 *
 * Keying on the id alone would leave the old translation showing under a bubble
 * whose words have since changed — which is the one failure mode of a
 * translation cache that actively misleads rather than merely disappoints.
 */
export function cacheKeyFor(message) {
  return `${message.id}:${message.editedAt ?? ''}`;
}

export function cachedTranslation(message) {
  return results.get(cacheKeyFor(message)) ?? null;
}

function detector() {
  // A rejected promise must not be cached, or one failure disables the feature
  // for the rest of the session.
  detectorPromise ??= self.LanguageDetector.create().catch((err) => {
    detectorPromise = null;
    throw err;
  });
  return detectorPromise;
}

async function translatorFor(sourceLanguage) {
  const existing = translators.get(sourceLanguage);
  if (existing) return existing;

  const created = (async () => {
    const availability = await self.Translator.availability({
      sourceLanguage,
      targetLanguage: TARGET_LANGUAGE,
    });
    if (availability === 'unavailable') {
      throw new Error(`Your browser cannot translate ${languageName(sourceLanguage)} yet.`);
    }
    // 'downloadable' resolves once the language pack has been fetched. The
    // click that got us here is the user activation the download requires.
    return self.Translator.create({ sourceLanguage, targetLanguage: TARGET_LANGUAGE });
  })().catch((err) => {
    translators.delete(sourceLanguage);
    throw err;
  });

  translators.set(sourceLanguage, created);
  return created;
}

/** `es` -> `Spanish`, falling back to the tag itself where Intl has no name. */
export function languageName(tag) {
  try {
    return new Intl.DisplayNames(undefined, { type: 'language' }).of(tag) ?? tag;
  } catch {
    return tag;
  }
}

/**
 * Translates one message into English.
 *
 * Resolves `{ text, sourceLanguage }` on success, or `{ alreadyEnglish: true }`
 * when there was nothing to do — said out loud rather than silently returning
 * the same string, so a click always produces an answer.
 *
 * `server` is the fallback for a browser with no translator of its own: an
 * async function resolving the same shape. It is passed in rather than imported
 * so this module stays free of the API client — and so the rule that it is only
 * ever *reached* by a browser that cannot translate locally is enforced here,
 * in one place, rather than at each call site.
 *
 * @throws {Error} with a sentence fit to put in front of an agent.
 */
export async function translateToEnglish(message, { server } = {}) {
  const body = message.body?.trim();
  if (!body) throw new Error('There is no text to translate.');

  const key = cacheKeyFor(message);
  const cached = results.get(key);
  if (cached) return { text: cached, cached: true };

  if (!translationAvailable()) {
    if (!server) throw new Error('This browser has no built-in translator.');

    const result = await server();
    if (result.alreadyEnglish) return { alreadyEnglish: true };
    // Same session-only cache as the local path: a reload forgets it, and
    // nothing is written to storage on the way through.
    results.set(key, result.text);
    return { text: result.text, sourceLanguage: result.sourceLanguage ?? null, viaServer: true };
  }

  const [best] = await (await detector()).detect(body);
  const source = best?.detectedLanguage;

  // 'und' is the detector saying it does not know — usually a bubble that is
  // three words long. Guessing a language and translating from it produces
  // confident nonsense, so it says so instead.
  if (!source || source === 'und') {
    throw new Error('Could not tell what language this is.');
  }
  if (source === TARGET_LANGUAGE) return { alreadyEnglish: true };

  const translator = await translatorFor(source);
  const text = await translator.translate(body);

  results.set(key, text);
  return { text, sourceLanguage: source };
}

export default {
  translationAvailable,
  translationSupported,
  translateToEnglish,
  cachedTranslation,
  cacheKeyFor,
};
